import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pin } from 'lucide-react';
import { HUB_PROVIDERS } from '@repo/shared-types';
import { SlackCard } from '../components/integrations/SlackCard';
import { IngestWindowCard } from '../components/integrations/IngestWindowCard';
import { ProviderSection } from '../components/sources/ProviderSection';
import { api, type Integration } from '../lib/api';
import { useCurrentProject } from '../lib/project';
import { useAuth } from '../lib/auth';
import { useSources } from '../lib/sources';

/**
 * What is pinned to *this* project: everything from these sources is filed here, whatever it is
 * about. A tab of the project's settings — ProjectSettingsLayout owns the heading.
 */
export default function Integrations() {
  const { project } = useCurrentProject();
  const { hasFeature } = useAuth();
  const { overview, error, busyKey, save } = useSources();
  const [slack, setSlack] = useState<Integration | null>(null);

  const loadSlack = useCallback(() => {
    if (!project) return;
    api.integrations
      .list(project.slug)
      .then((rows) => setSlack(rows.find((r) => r.provider === 'SLACK' && r.scope === 'PROJECT') ?? null))
      .catch(() => setSlack(null));
  }, [project?.slug]);
  useEffect(loadSlack, [loadSlack]);

  if (!project) return null;
  const pinTo = { id: project.id, name: project.name };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 rounded-lg border border-border bg-surface-subtle px-4 py-3 text-xs text-ink-muted leading-relaxed">
        <Pin size={15} className="text-orange-500 mt-0.5 flex-shrink-0" />
        <p>
          Everything from a source pinned here is filed into{' '}
          <span className="font-medium text-ink-secondary">{project.name}</span>, whatever it is about.
          {hasFeature('GLOBAL_INTEGRATIONS') && (
            <>
              {' '}To let the ingestor decide per item instead, receive the source for the organization on{' '}
              <Link to="/sources" className="text-orange-500 hover:text-orange-600 underline">
                Sources
              </Link>
              .
            </>
          )}
        </p>
      </div>

      <SlackCard scope="PROJECT" projectSlug={project.slug} projectName={project.name} integration={slack} onChanged={loadSlack} />

      {error && <p className="text-sm text-danger-text bg-danger-bg px-3 py-2 rounded-lg">{error}</p>}

      {overview?.hub.reachable &&
        HUB_PROVIDERS.filter((p) => p !== 'GOOGLE_CALENDAR').map((provider) => (
          <ProviderSection
            key={provider}
            provider={provider}
            sources={overview.sources.filter((s) => s.provider === provider && s.supplied !== false)}
            consoleUrl={overview.hub.consoleUrl}
            busyKey={busyKey}
            onSave={save}
            pinTo={pinTo}
          />
        ))}

      <IngestWindowCard projectId={project.id} projectName={project.name} />
    </div>
  );
}
